<script>
  import { createEventDispatcher } from 'svelte';
  import { fade } from 'svelte/transition';
  import { spring } from 'svelte/motion';

  const dispatch = createEventDispatcher();
  
  export let isRecording = false;
  export let question = '';
  export let isMobile = false; // New prop to check if we're on mobile

  const pulse = spring({ scale: 1 }, {
    stiffness: 0.1,
    damping: 0.15
  });

  $: if (isRecording) {
    pulse.set({ scale: 1.05 });
  } else {
    pulse.set({ scale: 1 });
  }
  
  let isWaitingForPermissions = false;

  async function handleRecordInteraction(event) {
    event.preventDefault();
    
    if (isWaitingForPermissions) return;
    
    if (isRecording) {
      dispatch('submit');
    } else {
      isWaitingForPermissions = true;
      dispatch('recordToggle');
      setTimeout(() => {
        isWaitingForPermissions = false;
      }, 2000);
    }
  }

  function handleSubmit(event) {
    event.preventDefault();
    if (!isRecording && question.trim()) {
      dispatch('submit');
    }
  }

  function handleCancel(event) {
    event.preventDefault();
    dispatch('cancel');
  }
</script>

<div class="modal-backdrop" 
   transition:fade 
   on:touchstart|stopPropagation 
   on:click|stopPropagation>

{#if !isMobile}
  <!-- Only show record button on desktop -->
  <button 
    class="record-button {isRecording ? 'recording' : ''} {isWaitingForPermissions ? 'waiting' : ''}"
    style="transform: scale({$pulse.scale})"
    on:click={handleRecordInteraction}
    on:touchstart={handleRecordInteraction}
    disabled={isWaitingForPermissions}
  >
    <div class="microphone">🎙️</div>
    <span class="record-text">
      {#if isWaitingForPermissions}
        waiting for mic access...
      {:else if isRecording}
        tap to finish
      {:else}
        tap to ask aloud
      {/if}
    </span>
  </button>
{/if}

<div class="text-input-section" class:disabled={isRecording} class:mobile={isMobile}>
  <textarea
    bind:value={question}
    placeholder={isMobile ? "Type your question here..." : "Or type your question here..."}
    disabled={isRecording}
    on:touchstart|stopPropagation
  />
  <button 
    class="submit"
    on:click={handleSubmit}
    on:touchstart={handleSubmit}
    disabled={isRecording || !question.trim()}
  >
    Submit
  </button>
</div>

<button 
  class="cancel" 
  on:click={handleCancel}
  on:touchstart={handleCancel}
>
  Cancel
</button>
</div>

<style>
  .modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.2);
    backdrop-filter: blur(8px);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2rem;
    z-index: 1000;
  }

  .record-button {
    width: 200px;
    height: 200px;
    background: white;
    border: none;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    transition: all 0.2s ease;
    position: relative;
  }

  .record-button.waiting {
      opacity: 0.7;
      cursor: wait;
  }

  .microphone {
    font-size: 3rem;
  }

  .record-text {
    font-size: 1rem;
    color: #4b5563;
  }

  .record-button.recording {
    background: rgb(254, 226, 226);
  }

  .record-button.recording::after {
    content: '';
    position: absolute;
    inset: -4px;
    border-radius: 50%;
    border: 2px solid rgb(255, 0, 0);
    animation: pulse 2s ease-out infinite;
  }

  @keyframes pulse {
    0% {
      transform: scale(1);
      opacity: 1;
    }
    100% {
      transform: scale(1.5);
      opacity: 0;
    }
  }

  .text-input-section {
    width: 90%;
    max-width: 500px;
    background: white;
    padding: 1rem;
    border-radius: 1rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    transition: opacity 0.3s ease;
  }

  /* New mobile styles */
  .text-input-section.mobile {
    margin-top: 2rem;
    width: 95%;
  }

  .text-input-section.disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  textarea {
    width: 100%;
    height: 100px;
    padding: 0.75rem;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    margin-bottom: 1rem;
    resize: none;
    font-size: 1rem;
  }

  button.submit {
    width: 100%;
    padding: 0.75rem;
    background-color: #2563eb;
    color: white;
    border: none;
    border-radius: 0.5rem;
    cursor: pointer;
    font-weight: 500;
    transition: opacity 0.2s ease;
  }

  button.submit:hover:not(:disabled) {
    opacity: 0.9;
  }

  button.cancel {
    background: white;
    color: #4b5563;
    border: none;
    border-radius: 0.5rem;
    padding: 0.75rem 2rem;
    cursor: pointer;
    font-weight: 500;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    transition: background-color 0.2s ease;
  }

  button.cancel:hover {
    background-color: #f3f4f6;
  }

  textarea:disabled {
    background-color: #f3f4f6;
    cursor: not-allowed;
  }

  button:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }

  button {
      -webkit-tap-highlight-color: transparent;
  }
  
  .record-button,
  button.submit,
  button.cancel {
      min-height: 44px;
      touch-action: manipulation;
  }
  
  * {
      -webkit-touch-callout: none;
      -webkit-user-select: none;
      user-select: none;
  }
  
  textarea {
      -webkit-user-select: text;
      user-select: text;
  }
</style>